// /components/forms/TruckForm.tsx (UPDATED)

import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Select,
    VStack,
    HStack,
    Text,
    Grid,
    FormHelperText,
} from '@chakra-ui/react';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';

interface TruckFormInputs {
    plate_number: string;
    condition: string;
    is_active: number; 
}

interface TruckData {
    id: number;
    plate_number: string;
    condition: string;
    is_active: number;
}

interface TruckFormProps {
    truckId?: number; 
    initialData?: TruckData; 
    onSuccess?: () => void;
    onCancel?: () => void;
}

const conditionOptions = [
    'Excellent',
    'Good',
    'Fair',
    'Needs Repair',
    'Under Maintenance',
];

export function TruckForm({ truckId, initialData, onSuccess, onCancel }: TruckFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showError, showSuccess } = useToastNotification();
    
    const isEditMode = !!truckId; 
    
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<TruckFormInputs>({
        defaultValues: initialData ? {
            plate_number: initialData.plate_number,
            condition: initialData.condition,
            is_active: initialData.is_active,
        } : {
            plate_number: '',
            condition: '',
            is_active: 1, 
        },
    });

    // useEffect(() => {
    //     if (initialData) {
    //         reset({
    //             plate_number: initialData.plate_number,
    //             condition: initialData.condition,
    //             is_active: initialData.is_active,
    //         });
    //     }
    // }, [initialData, reset]);

    useEffect(() => {
        if (initialData) {
            reset({
                // Fallback to camelCase if snake_case is missing
                plate_number: initialData.plate_number  || '',
                condition: initialData.condition || '',
                is_active: initialData.is_active !== undefined ? Number(initialData.is_active) : 1,
            });
        }
    }, [initialData, reset]);


    // --- Form Submission Handler ---
    const onSubmit: SubmitHandler<TruckFormInputs> = async (data) => {
        setIsSubmitting(true);
        try {
            const payload: any = {
                plate_number: data.plate_number,
                condition: data.condition,
            };
            
            let endpoint: string;
            let successMessage: string;

            if (isEditMode) {
                // ✅ USING NEW UPDATE ENDPOINT
                payload.is_active = Number(data.is_active); // Include status for update
                endpoint = `/trucks/update-truck/${truckId}`; 
                await apiClient.put(endpoint, payload); // Assuming your update is a PUT request
                successMessage = `Truck ${data.plate_number} updated successfully.`;

            } else {
                endpoint = '/trucks/add-truck';
                await apiClient.post(endpoint, payload);
                successMessage = `Truck ${data.plate_number} created successfully.`;
            }

            showSuccess('Success', successMessage);
            onSuccess?.();
            
        } catch (error: any) {
            showError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box as="form" onSubmit={handleSubmit(onSubmit)} maxW="700px">
            <VStack spacing={4} align="stretch">
                
                {/* Plate Number */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel pt={2} fontWeight="medium">
                        Plate Number <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.plate_number}>
                        <Input 
                            {...register('plate_number', 
                                {   required: 'Plate number is required',
                                   pattern: {
                                        // This RegEx ensures:
                                        // 1. Starts with letters (any number of letters, usually 3)
                                        // 2. Followed by a dash '-'
                                        // 3. Ends with 3 or 4 numbers
                                        value: /^[A-Z]+\-[0-9]{3,4}$/i,
                                        message: 'Format must be letters-numbers (e.g., ABC-1234 or ABC-123)'
                                    }
                                 })} 
                            placeholder="e.g. ABC-1234" 
                            isDisabled={isEditMode}
                            onChange={(e) => {
                                // Automatically force uppercase for consistency
                                e.target.value = e.target.value.toUpperCase();
                            }}
                        />
                        <FormErrorMessage>{errors.plate_number?.message}</FormErrorMessage>
                        {!errors.plate_number && (
                            <FormHelperText>Required format: LETTERS-NUMBERS</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Condition */}
                <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                    <FormLabel pt={2} fontWeight="medium">
                        Condition <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <FormControl isInvalid={!!errors.condition}>
                        <Select
                            {...register('condition', { required: 'Condition is required' })}
                            placeholder="Select Condition"
                        >
                            {conditionOptions.map((cond) => (
                                <option key={cond} value={cond}>
                                    {cond}
                                </option>
                            ))}
                        </Select>
                        <FormErrorMessage>{errors.condition?.message}</FormErrorMessage>
                    </FormControl>
                </Grid>

                {/* Status - Conditionally shown in Edit Mode */}
                {/* {isEditMode && (
                    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
                        <FormLabel pt={2} fontWeight="medium">
                            Status
                        </FormLabel>
                        <FormControl isInvalid={!!errors.is_active}>
                            <Select
                                {...register('is_active', { valueAsNumber: true })}
                            >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </Select>
                            <FormHelperText>
                                Control whether this truck is available for assignment.
                            </FormHelperText>
                            <FormErrorMessage>{errors.is_active?.message}</FormErrorMessage>
                        </FormControl>
                    </Grid>
                )} */}
                {isEditMode && (
                    <Grid 
                        templateColumns={{ base: '1fr', md: '3fr 9fr' }} 
                        gap={4} 
                        alignItems="start" // Changed from "center" to "start" for consistent top-alignment
                    >
                        <FormLabel pt={2} fontWeight="medium">
                            Status
                        </FormLabel>
                        
                        <FormControl isInvalid={!!errors.is_active}>
                            <Select
                                {...register('is_active', { valueAsNumber: true })}
                                bg="white" // Ensures consistent background look
                            >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </Select>
                            {/* Wrapping HelperText to ensure it doesn't affect label centering */}
                            <FormHelperText mt={2} lineHeight="short">
                                Control whether this truck is available for assignment.
                            </FormHelperText>
                            <FormErrorMessage>{errors.is_active?.message}</FormErrorMessage>
                        </FormControl>
                    </Grid>
                )}


                {/* --- Action Buttons --- */}
                <HStack spacing={4} justify="flex-end" pt={4}>
                    {onCancel && (
                        <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
                            Cancel
                        </Button>
                    )}
                    <Button 
                        type="submit" 
                        colorScheme="blue" 
                        isLoading={isSubmitting} 
                        loadingText={isEditMode ? 'Saving...' : 'Creating...'}
                    >
                        {isEditMode ? 'Save' : 'Create Truck'}
                        {isSubmitting && (
                            <Text as="span" ml={2} fontSize="sm">
                                ...
                            </Text>
                        )}
                    </Button>
                </HStack>
            </VStack>
        </Box>
    );
}